/* eslint-disable react/prop-types */
import {
  useCallback,
  useContext,
  useMemo,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import propTypes from 'prop-types';
import Swal from 'sweetalert2';

import ExamSort from './ExamSort';
import ExamSelect from './ExamSelect';
import ExamCheckBox from './ExamCheckBox';
import ExamMatch from './ExamMatch';
import ExamDraw from './ExamDraw';
import ExamFill from './ExamFill';
import ExamVoice from './ExamVoice';
import ExamFinishConfirmModal from './FinishConfirmModal';
import ExamNavigation from './Navigation';
import ExamQuestionNumbers from './QuestionNumbers';
import ExamQuestion from './Question';
import ExamQuestionFill from './QuestionFill';
import ExamDrag from './ExamDrag';
import ExamPdf from './ExamPdf';
// import ExamAls from './ExamAls';
// import ExamScale from './ExamScale';
import ExamQuestionDragPopup from './QuestionPopup';

import Card from 'components/Card';
import Typography from 'components/Typography';
import Button from 'components/Button';
// import WebCam from 'components/WebCam';
// import { IMAGE_MIME_TYPE } from 'constant';
import ExamControllerContext from 'context/examController.context';
import { call } from 'utils/caller';
import { secondsToTimerText } from 'libs/time';
import AppContext from 'context/app.context';
import clsx from 'libs/clsx';

function Exam({ isSimulation, onAnswerChange, onFinish }, ref) {
  const [{ isSidebarOpen }] = useContext(AppContext);
  const [
    { remainingTime, questions, answers, currentQuestionIndex },
    dispatch,
  ] = useContext(ExamControllerContext);

  const questionContainerRef = useRef();
  const answerContainerRef = useRef();
  const rightContainerRef = useRef();

  const [isFinishConfirmShow, setIsFinishConfirmShow] = useState(false);

  const answeredQuestions = useMemo(
    () => answers.filter((answer) => answer !== ' ').length,
    [answers]
  );

  const currentQuestion = useMemo(
    () => call(dispatch.getCurrentQuestion),
    [dispatch.getCurrentQuestion]
  );

  // Percentage of process.
  const percentage = useMemo(
    () =>
      Math.floor(
        (answers.filter((answer) => answer !== ' ').length / questions.length) *
          100
      ),
    [answers, questions.length]
  );

  const ExamQuestionReal = useMemo(() => {
    switch (currentQuestion.type) {
      case 'pil':
        return ExamQuestion;
      case 'sort':
        return ExamQuestion;
      case 'mlt':
        return ExamQuestion;
      case 'cck':
        return ExamQuestion;
      case 'cvs':
        return ExamQuestion;
      case 'isi':
        return ExamQuestionFill;
      case 'rec':
        return ExamQuestion;
      case 'drag':
        return ExamQuestionDragPopup;
      case 'pdf':
        return ExamQuestion;
      case 'als':
        return ExamQuestion;
      case 'lik':
        return ExamQuestion;
      case 'sjt':
        return ExamQuestion;
      case 'esy':
        return ExamQuestion;
      default:
        return () => <></>;
    }
  }, [currentQuestion.type]);

  // Memilih container berdasarkan jenis pertanyaan.
  const AnswerContainer = useMemo(() => {
    switch (currentQuestion.type) {
      case 'pil':
        return ExamSelect;
      case 'sort':
        return ExamSort;
      case 'mlt':
        return ExamCheckBox;
      case 'cck':
        return ExamMatch;
      case 'cvs':
        return ExamDraw;
      case 'isi':
        return ExamFill;
      case 'rec':
        return ExamVoice;
      case 'drag':
        return ExamDrag;
      case 'pdf':
        return ExamPdf;
      // case 'als':
      //   return ExamAls;
      // case 'lik':
      //   return ExamScale;
      // case 'sjt':
      //   return ExamScale;
      case 'esy':
        return ExamFill;
      default:
        return () => <></>;
    }
  }, [currentQuestion.type]);

  // Menangani apabila tombol `Sebelumnya` diklik.
  const handlePreviousButtonClick = useCallback(() => {
    if (currentQuestionIndex > 0) {
      answerContainerRef?.current?.componentWillUnmount();
      call(dispatch.prevQuestion);
    }
  }, [currentQuestionIndex, dispatch.prevQuestion]);

  // Menangani apabila tombol `Selanjutnya` diklik.
  const handleNextButtonClick = useCallback(() => {
    if (currentQuestionIndex + 1 < questions.length) {
      answerContainerRef?.current?.componentWillUnmount();
      call(dispatch.nextQuestion);
    }
  }, [currentQuestionIndex, dispatch.nextQuestion, questions.length]);

  // Menangani apabila tombol nomor pertanyaan diklik.
  const handleQuestionIndexChange = useCallback(
    (index) => {
      answerContainerRef?.current?.componentWillUnmount();
      call(dispatch.goToQuestionIndex, index);
    },
    [dispatch.goToQuestionIndex]
  );

  // Menangani apabila jawaban diubah.
  const handleAnswerChange = useCallback(
    (newAnswer) => onAnswerChange(newAnswer),
    [onAnswerChange]
  );

  // Reset answer by " ".
  const handleResetAnswer = useCallback(
    () => onAnswerChange(' '),
    [onAnswerChange]
  );

  // Mengupdate pertanyaan.
  const handleRefreshClick = useCallback(
    async ({ done }) => {
      if (!isSimulation) {
        await call(dispatch.refreshCurrentQuestion);
      }

      done();
    },
    [dispatch.refreshCurrentQuestion, isSimulation]
  );

  const handleFinishButtonClick = useCallback(() => {
    let canFinish = true;

    if (['sort', 'cvs'].includes(currentQuestion.type)) {
      answerContainerRef?.current?.componentWillUnmount();
    }

    const hasQuestionDrag = questions.some(
      (question) => question.type === 'drag'
    );

    // Question drag should be required
    if (hasQuestionDrag) {
      const questionDragIndexes = questions
        .map((question, index) => ({ ...question, index }))
        .filter((question) => question.type === 'drag')
        .map((question) => question.index);

      const answerDragInvalidIndexes = answers
        .map((answer, index) => ({ answer, index }))
        .filter((answer, index) => questionDragIndexes.includes(index))
        .filter((answer) => answer.answer.includes(' '));

      if (answerDragInvalidIndexes.length > 0) {
        // separate with "comma" and "and"
        const questionNumbers = answerDragInvalidIndexes
          .map((answer) => answer.index + 1)
          .reduce((acc, curr, index, arr) => {
            if (index === arr.length - 1) {
              return `${acc} dan ${curr}`;
            }
            return `${acc}, ${curr}`;
          });

        Swal.fire({
          icon: 'warning',
          // title: '.',
          text: `Anda belum menyelesaikan pertanyaan nomor ${questionNumbers}`,
        });

        canFinish = false;
      }
    }

    if (canFinish) {
      setIsFinishConfirmShow(true);
    }
  }, [answers, currentQuestion.type, questions]);

  const handleFinishConfirmCancel = useCallback(
    () => setIsFinishConfirmShow(false),
    []
  );

  const handleFinishConfirmed = useCallback(() => {
    onFinish();
  }, [onFinish]);

  useImperativeHandle(
    ref,
    () => ({
      changeQuestionIndex: (index) => handleQuestionIndexChange(index),
    }),
    [handleQuestionIndexChange]
  );

  const ZOOM_DEFAULT = 1;
  const ZOOM_MIN = 0.5;
  const ZOOM_MAX = 2.0;
  const ZOOM_STEP = 0.1;

  const [zoomLevel, setZoomLevel] = useState(ZOOM_DEFAULT);

  const zoomChange = (newZoom) => {
    document.body.style.zoom = newZoom;
  };

  const handleZoomIn = () => {
    const newZoomLevel = Math.min(zoomLevel + ZOOM_STEP, ZOOM_MAX);
    setZoomLevel(newZoomLevel);
    zoomChange(newZoomLevel);
  };

  const handleZoomOut = () => {
    const newZoomLevel = Math.max(zoomLevel - ZOOM_STEP, ZOOM_MIN);
    setZoomLevel(newZoomLevel);
    zoomChange(newZoomLevel);
  };

  const handleResetZoom = () => {
    setZoomLevel(ZOOM_DEFAULT);
    zoomChange(ZOOM_DEFAULT);
  };

  return (
    <>
      <div className="grid grid-cols-12 gap-4 xl:h-screen py-4">
        <div
          className={clsx(
            'xl:h-full h-screen overflow-y-auto',
            isSidebarOpen && '2xl:col-span-9 xl:col-span-8 col-span-12',
            !isSidebarOpen && '2xl:col-span-9 xl:col-span-9 col-span-12'
          )}
        >
          <Card
            ref={questionContainerRef}
            className="flex flex-col h-full question-container"
            style={{ maxHeight: '95vh' }}
          >
            {/* Pertanyaan */}
            <ExamQuestionReal
              ref={answerContainerRef}
              isSimulation={isSimulation}
              currentQuestionIndex={currentQuestionIndex}
              question={currentQuestion}
              questionTotal={questions.length}
              onRefresh={handleRefreshClick}
              answer={answers[currentQuestionIndex]}
              onAnswerChange={handleAnswerChange}
              resetAnswer={handleResetAnswer}
              answerComponent={
                <div className="mt-7">
                  <AnswerContainer
                    ref={answerContainerRef}
                    question={currentQuestion}
                    answer={answers[currentQuestionIndex]}
                    onAnswerChange={handleAnswerChange}
                    resetAnswer={handleResetAnswer}
                  />
                </div>
              }
              examNavigation={
                <ExamNavigation
                  currentQuestionIndex={currentQuestionIndex}
                  percentage={percentage}
                  questionTotal={questions.length}
                  answeredQuestionTotal={answeredQuestions}
                  onPreviousButtonClick={handlePreviousButtonClick}
                  onNextButtonClick={handleNextButtonClick}
                />
              }
            />
          </Card>
        </div>

        {/* Right side container */}
        <div
          className={clsx(
            'gap-4 xl:h-full h-screen',
            isSidebarOpen && '2xl:col-span-3 xl:col-span-4 col-span-12',
            !isSidebarOpen && '2xl:col-span-3 xl:col-span-3 col-span-12'
          )}
          ref={rightContainerRef}
        >
          {/* Card jumlah soal */}
          <Card className="pt-4 h-full" style={{ maxHeight: '95vh' }}>
            <div className="flex flex-col gap-2 h-full">
              <div className="flex-none space-y-3 pb-2">
                <div className="flex items-center justify-center space-x-2">
                  <Button
                    type="button"
                    size="xs"
                    color="secondary"
                    className="shadow-none"
                    onClick={handleZoomIn}
                  >
                    <span className="flex items-center gap-1">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.5 9.5C7.70914 9.5 9.5 7.70914 9.5 5.5C9.5 3.29086 7.70914 1.5 5.5 1.5C3.29086 1.5 1.5 3.29086 1.5 5.5C1.5 7.70914 3.29086 9.5 5.5 9.5Z"
                          stroke="black"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10.5 10.5L8.32501 8.32501"
                          stroke="black"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M5.5 4V7"
                          stroke="black"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M4 5.5H7"
                          stroke="black"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Perbesar
                    </span>
                  </Button>
                  <Button
                    type="button"
                    size="xs"
                    color="secondary"
                    className="shadow-none"
                    onClick={handleZoomOut}
                  >
                    <span className="flex items-center gap-1">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5.5 9.5C7.70914 9.5 9.5 7.70914 9.5 5.5C9.5 3.29086 7.70914 1.5 5.5 1.5C3.29086 1.5 1.5 3.29086 1.5 5.5C1.5 7.70914 3.29086 9.5 5.5 9.5Z"
                          stroke="black"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10.5 10.5L8.32501 8.32501"
                          stroke="black"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M4 5.5H7"
                          stroke="black"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Perkecil
                    </span>
                  </Button>
                  <Button
                    type="button"
                    size="xs"
                    color="secondary"
                    className="shadow-none"
                    onClick={handleResetZoom}
                  >
                    <span className="flex items-center gap-1">
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10.5 6C10.5 6.89002 10.2361 7.76004 9.74162 8.50007C9.24715 9.24009 8.54434 9.81686 7.72208 10.1575C6.89981 10.4981 5.99501 10.5872 5.1221 10.4135C4.24918 10.2399 3.44736 9.81132 2.81802 9.18198C2.18869 8.55264 1.7601 7.75082 1.58647 6.87791C1.41283 6.00499 1.50195 5.10019 1.84254 4.27792C2.18314 3.45566 2.75991 2.75285 3.49994 2.25839C4.23996 1.76392 5.10999 1.5 6 1.5C7.26 1.5 8.465 2 9.37 2.87L10.5 4"
                          stroke="black"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <path
                          d="M10.5 1.5V4H8"
                          stroke="black"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Reset
                    </span>
                  </Button>
                </div>

                <div className="text-center px-5">
                  <Typography variant="small" color="muted">
                    Sisa Waktu
                  </Typography>
                  <Typography variant="subtitle1">
                    {secondsToTimerText(remainingTime)}
                  </Typography>
                </div>
                <div className="flex flex-col gap-3">
                  {/* Cancel button */}
                  <Button
                    type="button"
                    color="danger"
                    size="custom"
                    onClick={handleResetAnswer}
                    disabled={answers[currentQuestionIndex] === ' '}
                    block
                  >
                    Batalkan Jawaban
                  </Button>

                  {/* Finish button */}
                  <Button
                    size="custom"
                    type="button"
                    color="warning"
                    onClick={handleFinishButtonClick}
                    block
                  >
                    Selesai Ujian
                  </Button>
                </div>
              </div>

              {/* Title */}
              <Typography variant="subtitle2" className="flex-none font-medium">
                Peta Soal
              </Typography>

              <div className="relative flex-grow">
                {/* Question number list */}
                <ExamQuestionNumbers
                  currentQuestionIndex={currentQuestionIndex}
                  questions={questions}
                  answers={answers}
                  onNumberButtonClick={handleQuestionIndexChange}
                />
              </div>
            </div>
          </Card>

          {/* Webcam */}
          {/* <Card
            className="flex-none"
            style={{
              maxHeight: isTabletOrMobile ? 'auto' : '50%',
            }}
          >
            <WebCam
              audio={false}
              controls={false}
              screenshotFormat={IMAGE_MIME_TYPE}
            />
          </Card> */}
        </div>
      </div>

      {/* Finish confirmation modal */}
      <ExamFinishConfirmModal
        isOpen={isFinishConfirmShow}
        onClose={handleFinishConfirmCancel}
        onConfirm={handleFinishConfirmed}
      />
    </>
  );
}

Exam = forwardRef(Exam);

Exam.defaultProps = {
  isSimulation: false,
};

Exam.propTypes = {
  isSimulation: propTypes.bool,
};

export default Exam;
